import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/apiErrorUtils';

const workflowStatusLabel = (status) => {
  if (status === 'DRAFT') return 'Draft';
  if (status === 'PENDING_INSPECTOR_APPROVAL') return 'Pending inspection';
  if (status === 'REJECTED') return 'Rejected';
  if (status === 'APPROVED') return 'Approved';
  return status || 'Applied/Legacy';
};

const PurchaseOrderDeleteDialog = ({ open, onClose, targets = [], onDeleted }) => {
  const { showToast } = useToast();
  const [dependenciesLoading, setDependenciesLoading] = useState(false);
  const [dependencyResults, setDependencyResults] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchDependencies = useCallback(async () => {
    if (!targets.length) {
      setDependencyResults([]);
      return;
    }
    setDependenciesLoading(true);
    try {
      if (targets.length === 1) {
        const res = await axios.get(`/api/store/purchase-orders/${targets[0]._id}/delete-dependencies`);
        setDependencyResults([{
          poId: res.data.poId,
          poNumber: res.data.poNumber,
          dependencies: res.data.dependencies || [],
          dependencyCount: res.data.dependencyCount || 0
        }]);
      } else {
        const res = await axios.post('/api/store/purchase-orders/delete-dependencies', {
          ids: targets.map((po) => po._id)
        });
        setDependencyResults(res.data.results || []);
      }
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to load linked receiving records.'), 'error');
      setDependencyResults(targets.map((po) => ({
        poId: po._id,
        poNumber: po.poNumber,
        dependencies: [],
        dependencyCount: 0
      })));
    } finally {
      setDependenciesLoading(false);
    }
  }, [targets, showToast]);

  useEffect(() => {
    if (!open) {
      setDependencyResults([]);
      return;
    }
    fetchDependencies();
  }, [open, fetchDependencies]);

  const totalDependencyCount = useMemo(
    () => dependencyResults.reduce((sum, row) => sum + (row.dependencyCount || 0), 0),
    [dependencyResults]
  );

  const handleDelete = async () => {
    if (!targets.length) return;
    setDeleteLoading(true);
    try {
      const ids = targets.map((po) => po._id);
      const cascade = true;
      if (ids.length === 1) {
        await axios.delete(`/api/store/purchase-orders/${ids[0]}`, {
          params: { cascade: 'true' }
        });
        const poNumber = targets[0].poNumber || ids[0];
        showToast(
          totalDependencyCount > 0
            ? `Purchase order ${poNumber} and linked receiving records deleted`
            : `Purchase order ${poNumber} deleted`,
          'success'
        );
      } else {
        const res = await axios.post('/api/store/purchase-orders/batch-delete', { ids, cascade });
        const { deletedCount = 0, failedCount = 0, failed = [] } = res.data || {};
        if (deletedCount > 0 && failedCount === 0) {
          showToast(
            totalDependencyCount > 0
              ? `${deletedCount} purchase order(s) and linked receiving records deleted`
              : `${deletedCount} purchase order(s) deleted`,
            'success'
          );
        } else if (deletedCount > 0) {
          const failedLabels = failed.map((f) => f.poNumber || f.poId).join(', ');
          showToast(`${deletedCount} deleted, ${failedCount} failed (${failedLabels})`, 'warning');
        } else {
          const firstError = failed[0]?.message || 'Failed to delete purchase orders.';
          if (failed[0]?.dependencies?.length) {
            setDependencyResults((prev) => prev.map((row) => {
              const match = failed.find((f) => String(f.poId) === String(row.poId));
              if (!match) return row;
              return {
                ...row,
                dependencies: match.dependencies,
                dependencyCount: match.dependencies?.length || 0
              };
            }));
          }
          showToast(firstError, 'error');
          return;
        }
      }
      onDeleted?.();
      onClose?.();
    } catch (error) {
      const data = error.response?.data;
      if (data?.dependencies?.length && targets.length === 1) {
        setDependencyResults([{
          poId: data.poId || targets[0]._id,
          poNumber: data.poNumber || targets[0].poNumber,
          dependencies: data.dependencies,
          dependencyCount: data.dependencies.length
        }]);
      }
      showToast(getErrorMessage(error, 'Failed to delete purchase order(s).'), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !deleteLoading && onClose?.()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {targets.length > 1 ? `Delete ${targets.length} purchase orders` : 'Delete purchase order'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {targets.length > 1
            ? 'Review the purchase orders below before deleting.'
            : (
              <>
                Permanently delete purchase order <strong>{targets[0]?.poNumber}</strong>?
              </>
            )}
          {' '}This cannot be undone. Completed or received POs are allowed — linked receiving records
          will be removed and inventory quantities reversed.
        </Typography>

        {targets.length > 1 && (
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2 }}>
            {targets.map((po) => (
              <Typography key={po._id} component="li" variant="body2">
                {po.poNumber}
              </Typography>
            ))}
          </Box>
        )}

        {dependenciesLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Checking linked receiving records…
            </Typography>
          </Box>
        ) : totalDependencyCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {totalDependencyCount} receiving record{totalDependencyCount === 1 ? '' : 's'} linked to
            {' '}{targets.length === 1 ? 'this PO' : 'these POs'} will also be deleted.
            Applied records will have inventory and PO received quantities reversed.
          </Alert>
        )}

        {!dependenciesLoading && dependencyResults.some((row) => (row.dependencyCount || 0) > 0) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {dependencyResults.filter((row) => (row.dependencyCount || 0) > 0).map((row) => (
              <Box key={row.poId}>
                <Typography variant="subtitle2" gutterBottom>
                  PO {row.poNumber || row.poId} — linked receiving records
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Receiving date</TableCell>
                        <TableCell>Reference</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Received by</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(row.dependencies || []).map((dep) => (
                        <TableRow key={dep._id}>
                          <TableCell>
                            {dep.receivingDate ? new Date(dep.receivingDate).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell>{dep.reference || '—'}</TableCell>
                          <TableCell>
                            {dep.supplierName
                              ? `${dep.supplierName}${dep.supplierCode ? ` (${dep.supplierCode})` : ''}`
                              : '—'}
                          </TableCell>
                          <TableCell>{dep.receivedByName || '—'}</TableCell>
                          <TableCell>{workflowStatusLabel(dep.workflowStatus)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleteLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleteLoading || dependenciesLoading}
        >
          {deleteLoading
            ? 'Deleting…'
            : totalDependencyCount > 0
              ? `Delete PO${targets.length > 1 ? 's' : ''} and receiving (${totalDependencyCount})`
              : targets.length > 1
                ? `Delete ${targets.length} POs`
                : 'Delete PO'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseOrderDeleteDialog;
